import { getReviewPendingNavEntriesForFacility } from "../../../../constants/reviewPendingNavConfig";
import { HealthCareFacilityModel } from "../../../../data-model/healthFacility";
import {
  MedicalOrderReviewStatus,
  MedicalOrderStatus,
} from "../../../../enums/orderReviewStatus";

/**
 * Determines if the review button should be shown based on health facility settings
 * (review_pending_config), order status and assignment.
 * The order's master_auth_status must be one of the auth statuses in the facility's
 * review_pending_config (e.g. no_auth_required, auth_on_file). Each row may define its
 * own `review_bo_status` list for BO comparisons on save (any match counts).
 *
 * @param facility - The health facility (used for enable_review_mode and review_pending_config)
 * @param medicalOrderStatus - The medical order status
 * @param medicalOrderReviewStatus - The medical order review status
 * @param masterAuthStatus - The master auth status of the order
 * @param assignedToProviderId - The provider ID the order is assigned to
 * @param currentUserId - The current logged-in user's ID
 * @returns true if the review button should be shown, false otherwise
 */
export const shouldShowReviewButton = (
  facility: HealthCareFacilityModel | undefined,
  medicalOrderStatus?: string,
  medicalOrderReviewStatus?: string,
  masterAuthStatus?: string,
  assignedToProviderId?: string,
  currentUserId?: string,
): boolean => {
  const allowedEntries = getReviewPendingNavEntriesForFacility(facility);
  const allowedMasterAuthStatuses = allowedEntries.map(
    (e) => e.masterAuthStatus,
  );

  if (allowedMasterAuthStatuses.length === 0) return false;

  if (medicalOrderStatus === MedicalOrderStatus.ORDER_IN_PROGRESS) return false;

  let allowedMedicalOrderReviewStatuses = [
    MedicalOrderReviewStatus.AGENT_ORDER_REVIEW_PENDING,
    MedicalOrderReviewStatus.AGENT_ORDER_PASSED_BY_HUMAN,
    MedicalOrderReviewStatus.AGENT_ORDER_REJECTED_BY_HUMAN,
  ] as MedicalOrderReviewStatus[];

  return (
    !!medicalOrderReviewStatus &&
    allowedMedicalOrderReviewStatuses.includes(
      medicalOrderReviewStatus as MedicalOrderReviewStatus,
    ) &&
    !!masterAuthStatus &&
    allowedMasterAuthStatuses.includes(masterAuthStatus) &&
    assignedToProviderId === currentUserId
  );
};
